import React, { forwardRef } from 'react';
import { makeStyles, Button, Tooltip } from '@material-ui/core';
import { designTokens } from '../../../styles/designTokens';
import LoadIndicator from './LoadIndicator';
import ServiceIcons from './ServiceIcons';
import CollectionHours from './CollectionHours';
import { extractLoad } from '../utils/formatters';

const useStyles = makeStyles({
    card: {
        maxWidth: '924px',
        backgroundColor: designTokens.colors.background.card,
        borderRadius: designTokens.borderRadius.card,
        padding: designTokens.spacing.md,
        marginBottom: designTokens.spacing.lg,
        border: `1px solid ${designTokens.colors.border}`,
        transition: 'box-shadow 0.3s ease, border-color 0.3s ease',
        cursor: 'pointer',
        '&:hover': {
            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
        },
        '&:last-child': {
            marginBottom: '24px',
        },
        '@media (max-width: 900px)': {
            padding: designTokens.spacing.lg,
        }
    },
    cardSelected: {
        borderColor: designTokens.colors.primary,
        boxShadow: `0px 0px 0px 2px ${designTokens.colors.primary}20`,
    },
    content: {
        display: 'grid',
        gridTemplateColumns: '1fr 230px',
        gridGap: designTokens.spacing.xl,
        '@media (max-width: 1300px)': {
            gridTemplateColumns: '1fr',
            gridGap: designTokens.spacing.lg,
        }
    },
    leftSection: {
        display: 'flex',
        flexDirection: 'column',
        gap: designTokens.spacing.md,
    },
    address: {
        ...designTokens.typography.body,
        margin: 0,
        display: 'flex',
        alignItems: 'center',
        gap: designTokens.spacing.sm,
    },
    markerIcon: {
        width: 24,
        height: 24,
    },
    bottomRow: {
        display: 'flex',
        gap: '52px',
        alignItems: 'flex-start',
        '@media (max-width: 1300px)': {
            flexDirection: 'column',
            gap: designTokens.spacing.md,
        }
    },
    loadSection: {
        display: 'flex',
        flexDirection: 'column',
        gap: 0,
        width: '280px',
        flexShrink: 0,
        '@media (max-width: 900px)': {
            width: '100%',
        }
    },
    alertSection: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },
    alertIcon: {
        width: '24px',
        height: '24px',
        '& path': {
            stroke: designTokens.colors.red.main,
        }
    },
    servicesSection: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
    },
    tooltip: {
        backgroundColor: designTokens.colors.primary,
        fontSize: '12px',
    },
    arrow: {
        color: designTokens.colors.primary,
    },
    rightSection: {
        display: 'flex',
        alignItems: 'flex-start',
    },
    mapButton: {
        fontFamily: designTokens.fontFamily,
        fontSize: '11px',
        lineHeight: '16px',
        color: designTokens.colors.grey[300],
        textTransform: 'none',
        padding: 0,
        width: 'fit-content',
        '&:hover': {
            color: designTokens.colors.primary,
            backgroundColor: 'transparent',
            '& $mapIcon path': {
                stroke: designTokens.colors.primary,
            }
        },
    },
    mapIcon: {
        width: '20px',
        height: '20px',
        '& path': {
            stroke: designTokens.colors.grey[300],
            transition: 'stroke 0.2s ease',
        }
    }
});

/**
 * Компонент картки пункту забору
 * @param {Object} props
 * @param {Object} props.punkt - Об'єкт пункту
 * @param {boolean} props.isSelected - Чи обраний пункт
 * @param {Function} props.onClick - Callback при кліку на картку
 * @param {Function} props.onMapClick - Callback для відкриття карти
 */
const CollectionPointCard = forwardRef(({ punkt, isSelected, onClick, onMapClick }, ref) => {
    const classes = useStyles();
    const load = extractLoad(punkt.opt);

    const handleMapClick = (e) => {
        e.stopPropagation();
        if (onMapClick) {
            onMapClick(punkt);
        } else {
            const address = encodeURIComponent(punkt.address);
            window.open(`https://www.google.com/maps/search/?api=1&query=${address}`, '_blank');
        }
    };

    const handleCardClick = () => {
        if (onClick) {
            onClick(punkt);
        }
    };

    return (
        <div
            ref={ref}
            className={`${classes.card} ${isSelected ? classes.cardSelected : ''}`}
            onClick={handleCardClick}
        >
            <div className={classes.content}>
                <div className={classes.leftSection}>
                    <h3 className={classes.address}>
                        <img
                            src="/icons/collection-points/marker.svg"
                            alt="marker"
                            className={classes.markerIcon}
                        />
                        {punkt.address}
                    </h3>

                    <div className={classes.bottomRow}>
                        <div className={classes.loadSection}>
                            <LoadIndicator load={load} />
                            <Button
                                className={classes.mapButton}
                                onClick={handleMapClick}
                                endIcon={
                                    <svg className={classes.mapIcon} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M15 6.75L21 5.25V18.75L15 20.25M15 6.75L9 3.75M15 6.75V20.25M15 20.25L9 17.25M9 17.25V3.75M9 17.25L3 18.75V5.25L9 3.75" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                }
                            >
                                Переглянути на мапі
                            </Button>
                        </div>

                        {punkt.note && punkt.note.trim() && (
                            <div className={classes.alertSection}>
                                <Tooltip
                                    title={punkt.note}
                                    arrow
                                    placement="top"
                                    classes={{ tooltip: classes.tooltip, arrow: classes.arrow }}
                                >
                                    <svg className={classes.alertIcon} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                        <path d="M12 8V12" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                        <path d="M12 16H12.01" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                </Tooltip>
                            </div>
                        )}

                        <div className={classes.servicesSection}>
                            <ServiceIcons services={punkt.opt} />
                        </div>
                    </div>
                </div>

                <div className={classes.rightSection}>
                    <CollectionHours hWork={punkt.hWork} zWork={punkt.zWork} />
                </div>
            </div>
        </div>
    );
});

CollectionPointCard.displayName = 'CollectionPointCard';

export default CollectionPointCard;
